import { templateSettings } from "@/store/templateSetting";

/**
 * Normalize spacing shorthand to longhand format for comparison.
 * e.g. "0px" → "0px 0px 0px 0px", "10px 5px" → "10px 5px 10px 5px"
 */
export function normalizeSpacing(val: unknown): string | undefined {
  if (val === undefined || val === null || typeof val !== "string")
    return undefined;
  const parts = val.trim().split(/\s+/);
  if (parts.length === 1)
    return `${parts[0]} ${parts[0]} ${parts[0]} ${parts[0]}`;
  if (parts.length === 2)
    return `${parts[0]} ${parts[1]} ${parts[0]} ${parts[1]}`;
  if (parts.length === 3)
    return `${parts[0]} ${parts[1]} ${parts[2]} ${parts[1]}`;
  return val;
}

const ALL_ZEROS_REGEX = /^(0(px|%|em|rem)?\s*){4}$/;

/**
 * Check if a normalized spacing value is all zeros (e.g. "0px 0px 0px 0px").
 */
export function isZeroSpacing(val: unknown): boolean {
  const normalized = normalizeSpacing(val);
  return normalized !== undefined && ALL_ZEROS_REGEX.test(normalized.trim());
}

/**
 * Determine whether a block's attribute should be considered "unchanged"
 * (i.e. still matching the previous default) and therefore eligible for global update.
 */
export function shouldUpdateAttribute(
  currentValue: unknown,
  previousDefault: unknown
): boolean {
  if (currentValue === undefined || currentValue === previousDefault)
    return true;

  // If no default exists, treat all-zero spacing values as unchanged
  if (previousDefault === undefined) {
    return isZeroSpacing(currentValue);
  }

  // Handle spacing format differences (e.g. "0px" vs "0px 0px 0px 0px")
  if (typeof currentValue === "string" && typeof previousDefault === "string") {
    return normalizeSpacing(currentValue) === normalizeSpacing(previousDefault);
  }

  return false;
}

/**
 * Whether an attribute counts as "changed" from its default for the reset
 * indicator. This is the exact inverse of {@link shouldUpdateAttribute} so the
 * reset cross and the global settings-overwrite logic stay in sync: a value
 * that settings would overwrite (treated as unchanged) must not show the cross.
 * Notably, when no default exists an all-zero value is considered unchanged.
 */
export function wasAttrChanged(value: unknown, defaultValue: unknown): boolean {
  return !shouldUpdateAttribute(value, defaultValue);
}

/**
 * Resolve the previous default value for a block attribute.
 * Checks base attributes first, then falls back to templateSettings.
 */
export function resolvePreviousDefault(
  baseAttr: unknown,
  prop: string,
  block: string
): unknown {
  if (
    baseAttr &&
    !Array.isArray(baseAttr) &&
    typeof baseAttr === "object" &&
    prop in baseAttr
  ) {
    return (baseAttr as Record<string, unknown>)[prop];
  }

  const tagName = block as keyof typeof templateSettings;
  const blockDefaults = templateSettings[tagName];
  if (
    blockDefaults &&
    typeof blockDefaults === "object" &&
    prop in blockDefaults
  ) {
    return (blockDefaults as Record<string, unknown>)[prop];
  }

  return undefined;
}

/**
 * Recursively update attributes on nodes matching the given tagName,
 * but only if the node's current value is considered "unchanged".
 */
export function updateNodesRecursively(
  node: {
    tagName: string;
    attributes: Record<string, unknown>;
    children?: unknown[];
  },
  tagName: string,
  prop: string,
  value: unknown,
  previousDefault: unknown
): void {
  if (
    node.tagName === tagName &&
    shouldUpdateAttribute(node.attributes[prop], previousDefault)
  ) {
    if (prop !== "hover") {
      node.attributes[prop] = value;
    }
  }
  if (node.children) {
    for (const child of node.children) {
      updateNodesRecursively(
        child as typeof node,
        tagName,
        prop,
        value,
        previousDefault
      );
    }
  }
}

/**
 * The value a spacing-style input (padding, border-radius) should display.
 *
 * An absent value means "not overridden at this level", not "zero": the block
 * still renders with its effective default. Substituting a literal "0px" there
 * made the settings panels lie - the Button element defaults reported a corner
 * radius of 0 while every new button was created with the real default of 8px.
 *
 * An explicitly stored zero is a real override and is returned untouched.
 */
export function resolveDisplayedSpacing(
  value: string | undefined,
  defaultValue: string | undefined
): string {
  return value || defaultValue || "0px";
}
